import { useCallback, useEffect, useState } from "react";
import { Api, ApiError, type AgentSummary, type DeliveryState, type LeadDelivery, type LeadDetail as LeadDetailT, type LeadFilters, type LeadStatus, type LeadSummary } from "../api.js";
import { Badge } from "./ui.js";
import { ago } from "./Notifications.js";

const PAGE = 25;
const UTM_ORDER = ["source", "medium", "campaign", "term", "content"];

const STATUS_TEXT: Record<LeadStatus, { text: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  none: { text: "Saved only", tone: "muted" },
  pending: { text: "Sending…", tone: "info" },
  delivered: { text: "Delivered", tone: "ok" },
  partial: { text: "Partly delivered", tone: "warn" },
  failed: { text: "Not delivered", tone: "bad" },
};
const DELIVERY_TEXT: Record<DeliveryState, { text: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  queued: { text: "Waiting", tone: "info" },
  sending: { text: "Sending…", tone: "info" },
  sent: { text: "Sent", tone: "ok" },
  failed: { text: "Failed", tone: "bad" },
  not_connected: { text: "Not connected", tone: "warn" },
  skipped: { text: "Not sent", tone: "muted" },
};
const KIND_TEXT: Record<string, string> = {
  email: "Notification email",
  webhook: "Webhook (Zapier)",
  visitor_recap: "Copy to the visitor",
  wix: "Wix",
  hubspot: "HubSpot",
};

export const statusText = (s: LeadStatus) => STATUS_TEXT[s].text;
export const kindText = (k: string) => KIND_TEXT[k] ?? k;

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
/** The first details the owner asked for, to recognise a lead in the list. */
const headline = (l: LeadSummary) => l.fields.slice(0, 2).map((f) => f.value).join(" · ") || "(no details)";

export function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function Leads({ api }: { api: Api }) {
  const [filters, setFilters] = useState<LeadFilters>({});
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [items, setItems] = useState<LeadSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listAgents().then(setAgents, () => {});
  }, [api]);

  const load = useCallback(
    async (offset: number) => {
      setBusy(true);
      try {
        const r = await api.leads(filters, offset, PAGE);
        setItems((cur) => (offset === 0 ? r.leads : [...(cur ?? []), ...r.leads]));
        setTotal(r.total);
        setError(null);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Could not load the leads.");
      } finally {
        setBusy(false);
      }
    },
    [api, filters],
  );
  useEffect(() => {
    void load(0);
  }, [load]);

  const exportCsv = async () => {
    try {
      download("leads.csv", await api.exportLeads(filters));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not export.");
    }
  };

  const set = (patch: Partial<LeadFilters>) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <main className="page">
      <div className="row between">
        <h1>Leads</h1>
        <button className="btn secondary" onClick={() => void exportCsv()} disabled={!items?.length}>
          Export to CSV
        </button>
      </div>
      <p>Contact details your agents collected. Every lead is kept here, even if an email or a webhook fails.</p>
      {error && <div className="banner warn" role="alert">{error}</div>}

      <form className="row filters" onSubmit={(e) => e.preventDefault()} aria-label="Filter leads">
        <label className="inline">
          Agent{" "}
          <select aria-label="Agent" value={filters.agentId ?? ""} onChange={(e) => set({ agentId: e.target.value || undefined })}>
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || "Untitled agent"}
              </option>
            ))}
          </select>
        </label>
        <label className="inline">
          Delivery{" "}
          <select aria-label="Delivery status" value={filters.status ?? ""} onChange={(e) => set({ status: e.target.value as LeadStatus | "" })}>
            <option value="">Any</option>
            <option value="failed">Not delivered</option>
            <option value="partial">Partly delivered</option>
            <option value="pending">Sending</option>
            <option value="delivered">Delivered</option>
            <option value="none">Saved only</option>
          </select>
        </label>
        <label className="inline">
          From <input type="date" aria-label="From date" value={filters.from ?? ""} onChange={(e) => set({ from: e.target.value || undefined })} />
        </label>
        <label className="inline">
          To <input type="date" aria-label="To date" value={filters.to ?? ""} onChange={(e) => set({ to: e.target.value || undefined })} />
        </label>
        <input type="search" aria-label="Search leads" placeholder="Search name, email…" value={filters.q ?? ""} onChange={(e) => set({ q: e.target.value })} />
        <label className="inline">
          <input type="checkbox" checked={!!filters.test} onChange={(e) => set({ test: e.target.checked })} /> Show test leads
        </label>
      </form>

      {items === null && !error && <p>Loading…</p>}
      {items?.length === 0 && (
        <div className="card empty">
          <h2>No leads yet</h2>
          <p>{Object.values(filters).some(Boolean) ? "No leads match these filters." : "Turn on “Collect contact details” in the User data step of an agent. Leads appear here as soon as a visitor shares theirs."}</p>
        </div>
      )}
      {!!items?.length && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Lead</th>
                <th>Agent</th>
                <th>Source</th>
                <th>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id}>
                  <td>{when(l.createdAt)}</td>
                  <td>
                    <a href={`#/leads/${l.id}`}>{headline(l)}</a>
                    {l.isTest && <> <Badge tone="muted">Test</Badge></>}
                  </td>
                  <td>{l.agentName || "Deleted agent"}</td>
                  <td>{l.attribution?.label ?? "—"}</td>
                  <td>
                    <Badge tone={STATUS_TEXT[l.deliveryStatus].tone}>{STATUS_TEXT[l.deliveryStatus].text}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row between">
            <span className="hint">
              Showing {items.length} of {total}
            </span>
            {items.length < total && (
              <button className="btn secondary" onClick={() => void load(items.length)} disabled={busy}>
                {busy ? "Loading…" : "Show more"}
              </button>
            )}
          </div>
        </>
      )}
      <ErasePerson api={api} onDone={() => void load(0)} />
    </main>
  );
}

/** Data-protection request: remove everything about one person. */
function ErasePerson({ api, onDone }: { api: Api; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const run = async () => {
    try {
      const r = await api.eraseLeads(email.trim());
      setMsg(r.erased === 0 ? "Nothing found for that address." : `Deleted ${r.erased} lead${r.erased === 1 ? "" : "s"} and their conversations.`);
      setConfirm(false);
      setEmail("");
      onDone();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Could not delete.");
    }
  };
  return (
    <section className="card" aria-label="Delete a person's data">
      <button className="btn ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Delete everything about a person
      </button>
      {open && (
        <div>
          <p>For a privacy request: removes every lead that contains this email address, with its delivery log and the conversation it came from. Emails already sent and data already passed to Zapier are not recalled.</p>
          <div className="row">
            <input type="email" aria-label="Person's email address" placeholder="person@example.com" value={email} onChange={(e) => (setEmail(e.target.value), setConfirm(false), setMsg(null))} />
            {!confirm ? (
              <button className="btn secondary" disabled={!email.includes("@")} onClick={() => setConfirm(true)}>
                Delete…
              </button>
            ) : (
              <button className="btn primary" onClick={() => void run()}>
                Yes, delete permanently
              </button>
            )}
          </div>
          {msg && <p role="status">{msg}</p>}
        </div>
      )}
    </section>
  );
}

export function LeadDetail({ api, id }: { api: Api; id: number }) {
  const [lead, setLead] = useState<LeadDetailT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => api.lead(id).then((l) => (setLead(l), setError(null)), (e) => setError(e instanceof ApiError ? e.message : "Could not load this lead.")), [api, id]);
  useEffect(() => {
    void load();
  }, [load]);
  // While something is still being sent, look again every few seconds.
  const open = lead?.deliveries.some((d) => d.status === "queued" || d.status === "sending");
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [open, load]);

  const retry = async (deliveryId?: number) => {
    setBusy(true);
    try {
      await api.retryLead(id, deliveryId);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteLead(id);
      location.hash = "#/leads";
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not delete this lead.");
      setBusy(false);
    }
  };

  if (error && !lead) {
    return (
      <main className="page">
        <div className="banner warn" role="alert">{error}</div>
        <a href="#/leads">← All leads</a>
      </main>
    );
  }
  if (!lead) return <main className="page"><p>Loading…</p></main>;
  const canRetry = lead.deliveries.some((d) => d.status === "failed" || d.status === "skipped");

  return (
    <main className="page">
      <a href="#/leads" className="back">
        ← All leads
      </a>
      <div className="row between">
        <h1>
          {headline(lead)} {lead.isTest && <Badge tone="muted">Test</Badge>}
        </h1>
        <Badge tone={STATUS_TEXT[lead.deliveryStatus].tone}>{STATUS_TEXT[lead.deliveryStatus].text}</Badge>
      </div>
      {error && <div className="banner warn" role="alert">{error}</div>}
      <p className="hint">
        Received {when(lead.createdAt)} ({ago(lead.createdAt)}) · {lead.agentName || "Deleted agent"} · {{ web: "website chat", preview: "preview in the app", test: "test button" }[lead.channel] ?? lead.channel} · {lead.language === "es" ? "Spanish" : "English"}
      </p>

      <section className="card" aria-label="Details">
        <h2>Details</h2>
        <dl className="facts">
          {lead.fields.map((f) => (
            <div key={f.key}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {lead.attribution && <Attribution a={lead.attribution} />}

      {lead.source && (lead.source.pageUrl || lead.source.referrer) && (
        <section className="card" aria-label="Where the chat happened">
          <h2>Where the chat happened</h2>
          <dl className="facts">
            {lead.source.pageUrl && (
              <div>
                <dt>Page of the chat</dt>
                <dd>{lead.source.pageUrl}</dd>
              </div>
            )}
            {lead.source.firstPageUrl && lead.source.firstPageUrl !== lead.source.pageUrl && (
              <div>
                <dt>Page where the chat began</dt>
                <dd>{lead.source.firstPageUrl}</dd>
              </div>
            )}
            <div>
              <dt>Came from</dt>
              <dd>{lead.source.referrer ?? "Direct or unknown"}</dd>
            </div>
            {lead.source.utm && Object.keys(lead.source.utm).length > 0 && (
              <div>
                <dt>Campaign</dt>
                <dd>{UTM_ORDER.filter((k) => lead.source?.utm?.[k]).map((k) => `${k}: ${lead.source?.utm?.[k]}`).join(", ")}</dd>
              </div>
            )}
          </dl>
          <p className="hint">Reported by the chat window on your website. Shown as text, not as a link, because anyone can send any address.</p>
        </section>
      )}

      <section className="card" aria-label="Delivery">
        <div className="row between">
          <h2>Delivery</h2>
          {canRetry && (
            <button className="btn secondary" onClick={() => void retry()} disabled={busy}>
              Resend all that failed
            </button>
          )}
        </div>
        {lead.deliveries.length === 0 && <p>This agent had no email or webhook set up. The lead is saved here.</p>}
        <ul className="rows">
          {lead.deliveries.map((d) => (
            <DeliveryRow key={d.id} d={d} busy={busy} onRetry={() => void retry(d.id)} />
          ))}
        </ul>
      </section>

      {lead.excerpt.length > 0 && (
        <section className="card" aria-label="Conversation excerpt">
          <h2>Conversation</h2>
          <p className="hint">The last messages before the details were saved.</p>
          <ul className="rows">
            {lead.excerpt.map((m, i) => (
              <li key={i}>
                <strong>{m.role === "user" ? "Visitor" : lead.agentName || "Agent"}:</strong> {m.content}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card" aria-label="Delete this lead">
        {!confirmDelete ? (
          <button className="btn ghost" onClick={() => setConfirmDelete(true)}>
            Delete this lead
          </button>
        ) : (
          <div role="alertdialog" aria-label="Confirm delete">
            <p>
              <strong>Delete this lead?</strong> It is removed from the app permanently. Emails already sent and data already passed to Zapier are not recalled.
            </p>
            <div className="row">
              <button className="btn primary" onClick={() => void remove()} disabled={busy}>
                Delete lead
              </button>
              <button className="btn secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

const CLICK_IDS = ["gclid", "gbraid", "wbraid", "dclid", "fbclid", "msclkid", "ttclid", "twclid", "li_fat_id", "yclid", "epik", "scid", "irclickid"];
const TAGS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"];

/** The source of the first interaction. Everything here came from the visitor's browser, so it is text, never a link. */
function Attribution({ a }: { a: NonNullable<LeadDetailT["attribution"]> }) {
  const how = [a.source, a.medium].filter(Boolean).join(" / ");
  const tags = TAGS.filter((k) => a.params?.[k]).map((k) => `${k.replace("utm_", "")}: ${a.params?.[k]}`);
  const clicks = CLICK_IDS.filter((k) => a.params?.[k]).map((k) => `${k}: ${a.params?.[k]}`);
  return (
    <section className="card" aria-label="Where the visitor came from">
      <h2>Where the visitor came from</h2>
      <dl className="facts">
        <div>
          <dt>Source</dt>
          <dd>{a.label ?? "Unknown"}{how ? ` (${how})` : ""}</dd>
        </div>
        {a.landingUrl && (
          <div>
            <dt>Landing page</dt>
            <dd>{a.landingUrl}</dd>
          </div>
        )}
        {tags.length > 0 && (
          <div>
            <dt>Campaign tags</dt>
            <dd>{tags.join(", ")}</dd>
          </div>
        )}
        {clicks.length > 0 && (
          <div>
            <dt>Click ids</dt>
            <dd>{clicks.join(", ")}</dd>
          </div>
        )}
      </dl>
      <p className="hint">
        {a.firstSeenBy === "derived" ? "Estimated from older data, so it may be incomplete." : "Recorded from the first page where the chat window ran."}
        {a.confidence === "low" && a.firstSeenBy !== "derived" ? " Low confidence: no campaign tag or known referrer." : ""} Shown as text, not as a link, because anyone can send any address.
      </p>
    </section>
  );
}

function DeliveryRow({ d, busy, onRetry }: { d: LeadDelivery; busy: boolean; onRetry: () => void }) {
  const t = DELIVERY_TEXT[d.status];
  return (
    <li className="delivery">
      <div className="row between">
        <span>
          <strong>{kindText(d.kind)}</strong> <span className="hint">{d.target}</span>
        </span>
        <span className="row">
          <Badge tone={t.tone}>{t.text}</Badge>
          {(d.status === "failed" || d.status === "skipped") && (
            <button className="btn secondary" onClick={onRetry} disabled={busy} aria-label={`Resend ${kindText(d.kind)} to ${d.target}`}>
              Resend
            </button>
          )}
        </span>
      </div>
      {d.error && <p className="hint">{d.error}</p>}
      {d.status === "queued" && d.attempts > 0 && <p className="hint">Waiting to try again automatically.</p>}
      {d.status === "sent" && d.sentAt && <p className="hint">Sent {ago(d.sentAt)}</p>}
    </li>
  );
}
